import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "@/App.css";
import CoverScreen from "@/components/CoverScreen";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";

/* ------------------------------------------------------------------
   PREMIUM LATENCY™  —  Cinematic Investigative Microsite
   ------------------------------------------------------------------ */

const EVIDENCE = {
  whatsapp:  "https://customer-assets.emergentagent.com/job_mobile-cinematic-1/artifacts/dop5em9m_file_0000000089d8720bbc5352b2893f2380.png",
  form:      "https://customer-assets.emergentagent.com/job_mobile-cinematic-1/artifacts/eezgqpg1_file_00000000ffb071f8a521e923557dcb90.png",
  callLog1:  "https://customer-assets.emergentagent.com/job_mobile-cinematic-1/artifacts/q6pb48rn_Screenshot_20260513_145920_Phone.jpg",
  callLog2:  "https://customer-assets.emergentagent.com/job_mobile-cinematic-1/artifacts/ghrngyrs_Screenshot_20260512_195546_Phone.jpg",
  callLog3:  "https://customer-assets.emergentagent.com/job_mobile-cinematic-1/artifacts/ovab9a9u_Screenshot_20260512_195745_Phone.jpg",
  publicRate:    "https://customer-assets.emergentagent.com/job_mobile-cinematic-1/artifacts/gznb6zsq_Screenshot_20260505_134929_ixigo.jpg",
  conciergeRate: "https://customer-assets.emergentagent.com/job_mobile-cinematic-1/artifacts/f0vx6b7a_Screenshot_20260505_205618_Chrome.jpg",
};

const TIMELINE = [
  {
    intensity: 1,
    date: "05 MAY 2026",
    time: "11:20",
    title: "Initial Registration Failure",
    body:
      "First attempt to register for Mastercard Travel & Lifestyle Services (MTLS) through travel.mastercard.com. The platform returned a persistent server-side registration error immediately after successful form completion. The published resolution pathway redirected the cardholder to a hotline that subsequently proved inaccessible.",
    card: {
      label: "Observed Error",
      quote: "Sorry, something went wrong.",
    },
    meta: [
      { k: "Autofill completion", v: "< 1 second" },
      { k: "Result", v: "FAILED AT FINAL SUBMISSION", danger: true },
    ],
  },
  {
    intensity: 1,
    date: "05 MAY 2026",
    time: "11:22 – 12:30",
    title: "Repeated Failure Testing",
    body:
      "Approximately fifty registration attempts were conducted across multiple browsers, devices, autofill workflows, and separate network pathways. The server-side registration error persisted consistently across every permutation. No client-side cause could be reproduced or isolated.",
    listLabel: "Test matrix executed",
    list: [
      "multiple browser engines · Chromium, Gecko, WebKit",
      "multiple devices · desktop, mobile, tablet",
      "multiple network pathways · Jio, Airtel, fixed-line broadband",
      "autofill on · autofill off · manual entry retries",
      "incognito sessions · cleared cache · cleared cookies",
      "approximate attempts performed · ~50",
    ],
    footer:
      "Outcome — the failure surface was determined to reside server-side. No client-side configuration produced a successful registration.",
  },
  {
    intensity: 2,
    date: "05 MAY 2026",
    time: "11:49 – 11:55",
    title: "WhatsApp Concierge Deadlock",
    body:
      "Contact initiated with the Mastercard Travel & Lifestyle Services WhatsApp business account. Verification details were successfully provided. The concierge confirmed inability to locate any active MTLS profile and redirected the cardholder toward a separate hotline number for assistance, while the platform simultaneously prevented profile creation.",
    listLabel: "Operational contradiction observed",
    list: [
      "profile required to access concierge assistance",
      "concierge assistance required to create the profile",
      "platform simultaneously prevented profile creation",
      "hotline redirect issued to a number distinct from published India ITFS lines",
    ],
    footer: "Outcome — a recursive support deadlock with no functional self-service recovery path.",
  },
  {
    intensity: 3,
    date: "05 – 10 MAY 2026",
    time: "11:59 – 22:00",
    title: "Emergency Routing Failure",
    body:
      "Published ITFS and emergency escalation pathways demonstrated repeated call-routing failure and inaccessible support states. Attempts were made across separate dates and time windows from two Indian telecom carriers; outcomes were consistent across all permutations.",
    numbers: [
      { n: "0008 004 407 027", status: "Unreachable" },
      { n: "0008 001 001 087", status: "Routing Fail" },
      { n: "1800 627 8372", status: "Routing Fail" },
    ],
    listLabel: "Attempts conducted across",
    list: ["Jio network", "Airtel network", "multiple dates · 5, 6, 10 May", "same-minute retries"],
  },
  {
    intensity: 4,
    escalation: true,
    date: "05 MAY 2026",
    time: "15:34",
    title: "Initial Regional Escalation",
    body:
      "Documentation and escalation details were transmitted to regional Mastercard leadership — including Ka-Wai Au — regarding platform inaccessibility and the prevailing concierge deadlock conditions.",
    addressed: { name: "Ka-Wai Au", role: "Mastercard Global Customer Delivery leadership · Regional" },
    listLabel: "Primary concerns raised",
    list: [
      "persistent registration failures across ~50 attempts",
      "inaccessible India ITFS routing",
      "international hotline inaccessibility",
      "WhatsApp concierge deadlock",
      "inability to access World Mastercard travel benefits",
    ],
    card: {
      label: "Requested Resolution",
      quote: "I request your office to intervene and authorise manual profile creation.",
    },
    pill: { label: "Executive alias bounce", value: "550 5.1.1 User Unknown" },
  },
  {
    intensity: 2,
    date: "05 MAY 2026",
    time: "13:49",
    title: "Public Market Rate Capture",
    body:
      "Public booking channels displayed significantly lower pricing for the targeted Novotel Goa Resort & Spa reservation prior to manual concierge intervention. A booking-window snapshot was preserved as captured market rate evidence.",
    rate: {
      property: "Novotel Goa Resort & Spa  ·  4 Nights  ·  25–29 July 2026",
      original: "₹20,706",
      label: "Public market rate · pre-failure window",
    },
    listLabel: "Captured market rate evidence",
    list: [
      "public booking channels",
      "total inclusive of taxes",
      "live booking window · 19:45 minute price hold",
      "captured prior to MTLS access restoration",
    ],
    footer:
      "Outcome — a verifiable public-market baseline preserved before the support-failure sequence concluded.",
  },
  {
    intensity: 3,
    date: "06 MAY 2026",
    time: "Morning – Afternoon",
    title: "Concierge Assisted Account Handling",
    body:
      "Direct concierge engagement and manual handling pathways were initiated following continued onboarding failure. An MTLS Lifestyle Manager bypassed the standard onboarding flow and coordinated manual profile provisioning with the technology team.",
    card: {
      label: "Official Update",
      quote:
        "Your profile has been successfully created, and we have personally coordinated with our technology team to prioritize the activation of your access.",
      by: "MTLS Lifestyle Manager  ·  Ten Lifestyle Group",
    },
    footer:
      "This manual intervention directly contradicted earlier assertions that the MTLS platform was functioning normally.",
  },
  {
    intensity: 4,
    date: "06 MAY 2026",
    time: "Quoted Range",
    title: "Manual Quoted Price Escalation",
    body:
      "During concierge-assisted handling, verbally quoted hotel pricing increased materially compared to earlier captured public market rates. The same property, the same room category, and the same four-night window were referenced in both captures.",
    rate: {
      property: "Novotel Goa Resort & Spa  ·  Same 4-Night Window",
      original: "₹27,980 – ₹30,948",
      label: "Concierge-assisted pricing pathway",
    },
    card: {
      label: "Evidence Framing",
      quote:
        "Captured public pricing approximately ₹20,706 versus concierge-assisted quoted ranges approaching ₹27,980–₹30,948.",
    },
    listLabel: "Observed differential",
    list: [
      "public booking channels baseline · ₹20,706 (captured 05 May)",
      "concierge-assisted pricing pathway · ₹27,980 – ₹30,948 (06 May)",
      "approximate differential · ~₹10,000",
      "same property · same room category · same date window",
    ],
    footer:
      "The manually assisted concierge pathway resulted in materially higher quoted pricing compared to publicly accessible booking channels captured earlier during the incident window.",
  },
  {
    intensity: 4,
    date: "06 MAY 2026",
    time: "22:04",
    title: "Documented Response to Greg",
    body:
      "A comprehensive evidentiary response was transmitted to Greg with supporting documentation and chronology records attached. Regional leadership was copied for visibility, expanding the institutional reach of the file.",
    listLabel: "Supporting documentation attached",
    list: [
      "registration failure records",
      "call-log routing failure evidence",
      "concierge deadlock transcript",
      "captured market rate evidence",
      "support accessibility failure summary",
    ],
    card: {
      label: "Position Stated",
      quote:
        "My concern is not simply about normal market price fluctuation, but the fact that I was unable to access the MTLS platform and support channels during a time-sensitive booking window despite repeated attempts.",
    },
    footer: "Regional leadership visibility expanded during this phase.",
  },
  {
    intensity: 5,
    escalation: true,
    date: "09 MAY 2026",
    time: "08:14",
    title: "CEO Executive Escalation",
    body:
      "Formal executive escalation was transmitted to Mastercard CEO leadership alongside South Asia regional leadership contacts. The escalation explicitly cited disputed technical conclusions and the necessity of manual account provisioning to obtain baseline platform access.",
    listLabel: "Escalation cited",
    list: [
      "repeated registration failures",
      "inaccessible support infrastructure",
      "concierge deadlock",
      "disputed technical conclusions",
      "necessity of manual account provisioning",
    ],
    card: {
      label: "Key Observation",
      quote:
        "The need to escalate to executive leadership simply to obtain basic platform access is not consistent with a premium World Mastercard cardholder experience.",
    },
  },
  {
    intensity: 5,
    date: "10 MAY 2026",
    time: "20:19",
    title: "Sayo Executive Response",
    body:
      "The executive escalation response was acknowledged and an investigation pathway was formally confirmed by the Mastercard Executive Escalation team.",
    card: {
      label: "Official Response",
      quote:
        "The review remains ongoing with our internal teams and service provider. We will follow up once this work has concluded and the next steps have been determined.",
      by: "Sayo  ·  Mastercard Executive Escalation Team",
    },
    statusGrid: [
      { k: "Status", v: "Under Review", tone: "warn" },
      { k: "Resolution", v: "Unresolved", tone: "bad" },
      { k: "Findings", v: "Pending", tone: "neutral" },
    ],
  },
  {
    intensity: 5,
    date: "10 MAY 2026",
    time: "22:20",
    title: "Request for Timeline and Supporting Material",
    body:
      "A follow-up response was transmitted requesting an actionable remediation timeline and confirmation of the investigative review scope. Supplemental evidence was attached to support the file.",
    listLabel: "Follow-up request scope",
    list: [
      "actionable remediation timeline",
      "confirmation of investigative review scope",
      "internal vs vendor accountability delineation",
      "verified pricing impact records",
      "telecom routing evidence across Indian carriers",
    ],
    footer:
      "Operational concern reframed — isolated customer support issue → systemic accessibility failure.",
  },
  {
    intensity: 4,
    date: "15 MAY 2026",
    time: "01:23",
    title: "Documentation Notice Sent",
    body: "A notice was sent to Mastercard Executive Escalations advising that the documented experience had been compiled into a public editorial case-study format at premiumlatency.com. Relevant organisations were invited to provide clarification, context, or formal comment prior to broader publication review."
  },
  {
    intensity: 5,
    date: "19 MAY 2026",
    time: "",
    title: "Documentation Review Period",
    body: "As of 19 May 2026, no formal clarification or additional commentary had been incorporated into the published record. The documentation remained publicly accessible in its current form."
  },
  {
    intensity: 4,
    date: "21 MAY 2026",
    title: "HDFC Bank Social Media Contact",
    body: "Following contact through HDFC Bank's social media customer care channel, HDFC Bank requested additional information in order to review the matter."
  },
  {
    intensity: 4,
    date: "23 MAY 2026",
    title: "Formal HDFC Bank Grievance Registered",
    body: "A formal grievance was registered with HDFC Bank regarding Mastercard Travel & Lifestyle Services (MTLS) accessibility concerns associated with the card benefit ecosystem.",
    listLabel: "The grievance included:",
    list: [
      "Repeated MTLS registration and profile creation failures",
      "Inability to complete account creation despite multiple attempts",
      "Support dependency on an active profile while profile creation remained unsuccessful",
      "Concerns regarding support accessibility and escalation handling"
    ]
  },
  {
    intensity: 4,
    date: "23 MAY 2026",
    title: "Grievance Acknowledgement Received",
    body: "HDFC Bank Grievance Redressal Cell acknowledged the matter and assigned Reference Number: 26145254160. A response timeline was communicated."
  },
  {
    intensity: 4,
    date: "25 MAY 2026",
    title: "Consumer Grievance Submitted",
    body: "A consumer grievance relating to the documented service accessibility concerns was submitted through the National Consumer Helpline. Reference Number: 9385674."
  },
  {
    intensity: 4,
    date: "Late MAY 2026",
    title: "Public Documentation Expanded",
    body: "Additional chronology material, supporting records and escalation documentation were consolidated and published to improve transparency and preserve the sequence of events."
  },
  {
    intensity: 4,
    date: "06 JUN 2026",
    title: "HDFC Bank Grievance Response Received",
    body: "HDFC Bank issued a formal response under Reference 26145254160, providing a Mastercard World benefits guide together with benefit-related links and contact information. The supplied documentation addressed Mastercard World Golf, dining, and loyalty benefit programs. The provided resources were reviewed and found to be reachable. However, the response did not specifically address the MTLS registration failures, MTLS support accessibility, or the escalation history documented within this chronology."
  },
  {
    intensity: 4,
    date: "12 JUN 2026",
    title: "Escalation to Principal Nodal Officer",
    body: "Following review of HDFC Bank's grievance response, the matter was escalated to HDFC Bank's Principal Nodal Officer in accordance with the bank's published grievance redressal process."
  },
  {
    intensity: 4,
    date: "12 JUN 2026",
    title: "Mastercard Executive Escalation — No Further Update Received",
    body: "As of 12 June 2026, no substantive update had been received in response to the executive escalation initiated in May 2026. The formal notification issued on 15 May 2026 referencing premiumlatency.com had not resulted in any documented update being incorporated into this chronology."
  }
];

/* ------------------------------------------------------------------ */
function NavBar() {
  return (
    <nav className="nav-bar">
      <div className="nav-inner">
        <a href="#top" className="nav-brand">
          <span className="relative inline-block w-5 h-5 md:w-6 md:h-6 shrink-0">
            <span className="absolute inset-0 rounded-full border border-[var(--gold-dim)]" />
            <span className="absolute inset-[5px] rounded-full bg-[var(--gold)] opacity-70" />
          </span>
          <span>
            Premium Latency<sup className="font-mono text-[8px] md:text-[9px] text-[var(--gold)]">TM</sup>
          </span>
        </a>
        <div className="hidden lg:flex items-center gap-8 font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--ink-muted)]">
          <a href="#chronology" className="hover:text-[var(--gold)] transition-colors">Chronology</a>
          <a href="#deadlock" className="hover:text-[var(--gold)] transition-colors">Deadlock</a>
          <a href="#evidence" className="hover:text-[var(--gold)] transition-colors">Evidence</a>
          <a href="#escalation" className="hover:text-[var(--gold)] transition-colors">Escalation</a>
          <a href="#metrics" className="hover:text-[var(--gold)] transition-colors">Metrics</a>
          <a href="#disclaimer" className="hover:text-[var(--gold)] transition-colors">Notice</a>
        </div>
        <div className="nav-status">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--red)] shadow-[0_0_10px_var(--red-glow)] animate-pulse" />
          <span className="text-[#e9b8be]">Under Review</span>
        </div>
      </div>
    </nav>
  );
}

/* ------------------------------------------------------------------ */
function Spinner({ percent = 92 }) {
  const r = 70;
  const c = 2 * Math.PI * r;
  const off1 = c * (1 - percent / 100);
  return (
    <div className="loader-wrap" aria-hidden="true">
      <div className="spinner">
        <svg viewBox="0 0 160 160">
          <circle cx="80" cy="80" r={r} fill="none" strokeWidth="2" className="spinner-track" />
          <circle
            cx="80" cy="80" r={r} fill="none" strokeWidth="2"
            className="spinner-bar"
            strokeDasharray={c}
            strokeDashoffset={off1}
          />
          <circle
            cx="80" cy="80" r={r - 8} fill="none" strokeWidth="1"
            className="spinner-bar-2"
            strokeDasharray={c * 0.25}
            strokeDashoffset={-c * 0.5}
          />
        </svg>
        <div className="spinner-core">
          <div>
            <div className="spinner-percent">{percent}%</div>
            <div className="spinner-label">Loading</div>
          </div>
        </div>
      </div>
      <div className="spinner-status">
        <span className="line"><span className="live">●</span> Retrying Connection</span>
        <span className="sep" aria-hidden="true">·</span>
        <span className="line">Attempt 47 of 50</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function Hero() {
  return (
    <section className="hero" id="top">
      <div className="hero-inner">
        <div className="hero-grid">
          <div>
            <div className="hero-eyebrow">
              <span>Operational Case Study</span>
              <span className="dot">·</span>
              <span>India Region</span>
              <span className="dot">·</span>
              <span>May 2026</span>
            </div>

            <h1 className="hero-title">
              Premium <br />
              <span className="accent">Latency</span>
              <sup>TM</sup>
            </h1>

            <p className="hero-sub">Mastercard World: The Localhost Experience</p>

            <p className="hero-lede">
              A documented operational case study involving MTLS onboarding infrastructure,
              concierge routing architecture, and executive escalation handling. Observed
              May 2026, India region.
            </p>

            <div className="hero-badges">
              <span className="hero-badge is-status">
                <span className="pulse" />
                MTLS Status: Under Review
              </span>
              <span className="hero-badge">Vendor: Ten Lifestyle Group plc</span>
              <span className="hero-badge">LSE: TENL</span>
            </div>
          </div>

          <Spinner percent={92} />
        </div>
      </div>

      <div className="scroll-cue">
        <span>Scroll</span>
        <span className="line" />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
function TimelineEntry({ item }) {
  const renderShell = (children) => (
    item.escalation ? <div className="tl-shell">{children}</div> : <>{children}</>
  );

  return (
    <article
      className={`tl-entry reveal ${item.escalation ? "is-escalation" : ""}`}
      data-intensity={item.intensity}
    >
      <span className="tl-node" />
      {renderShell(
        <>
          <div className="tl-time">
            <span>{item.date}</span>
            {item.time && <span className="sep">·</span>}
            {item.time && <span>{item.time}</span>}
          </div>
          <h3 className="tl-heading">{item.title}</h3>
          <p className="tl-body">{item.body}</p>

          {item.addressed && (
            <div className="tl-card">
              <div className="tl-card-label">Addressed to</div>
              <div className="mt-3 text-[18px] font-semibold text-[var(--ink)]">{item.addressed.name}</div>
              <div className="mt-1 font-mono text-[11px] text-[var(--ink-muted)] tracking-wider">
                {item.addressed.role}
              </div>
            </div>
          )}

          {item.card && (
            <div className="tl-card">
              <div className="tl-card-label">{item.card.label}</div>
              <div className="tl-quote">"{item.card.quote}"</div>
              {item.card.by && <div className="tl-quote-by">— {item.card.by}</div>}
            </div>
          )}

          {item.numbers && (
            <div className="tl-card">
              <div className="tl-card-label">Numbers Tested</div>
              <div className="mt-3 grid gap-2">
                {item.numbers.map((n, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-2 border-b border-dashed border-[var(--line)] last:border-b-0">
                    <span className="font-mono text-[14px] text-[var(--ink)] tracking-wider">{n.n}</span>
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#e9a4ab] border border-[rgba(198,71,82,0.4)] px-2 py-1 bg-[rgba(198,71,82,0.06)]">
                      {n.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {item.rate && (
            <div className="tl-card">
              <div className="tl-card-label">{item.rate.property}</div>
              <div className="mt-3 flex items-baseline gap-4">
                <span className="font-semibold text-[28px] text-[var(--ink)] tracking-tight">{item.rate.original}</span>
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--ink-faint)]">
                  {item.rate.label}
                </span>
              </div>
            </div>
          )}

          {item.list && (
            <>
              {item.listLabel && (
                <div className="mt-5 font-mono text-[10.5px] tracking-[0.28em] uppercase text-[var(--ink-faint)]">
                  {item.listLabel}
                </div>
              )}
              <ul className={`tl-list ${item.list.length > 3 ? "is-2col" : ""}`}>
                {item.list.map((li, i) => (
                  <li key={i}>{li}</li>
                ))}
              </ul>
            </>
          )}

          {item.pill && (
            <div className="mt-5">
              <span className="tl-pill">
                <span className="pulse" />
                {item.pill.label}
                <span className="text-[var(--ink-muted)] ml-2 normal-case tracking-normal font-mono">
                  {item.pill.value}
                </span>
              </span>
            </div>
          )}

          {item.statusGrid && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {item.statusGrid.map((s, i) => (
                <div key={i} className="px-4 py-3 border border-[var(--line)] bg-[rgba(241,236,224,0.02)]">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--ink-faint)]">
                    {s.k}
                  </div>
                  <div className={`mt-1 font-mono text-[12px] tracking-[0.18em] uppercase ${
                    s.tone === "bad" ? "text-[#e9a4ab]" :
                    s.tone === "warn" ? "text-[#d8b46a]" :
                    "text-[var(--ink-muted)]"
                  }`}>
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          )}

          {item.footer && (
            <p className="mt-5 font-mono text-[11.5px] tracking-[0.06em] text-[var(--ink-faint)] leading-relaxed">
              {item.footer}
            </p>
          )}
        </>
      )}
    </article>
  );
}

/* ------------------------------------------------------------------ */
function SectionChronology() {
  return (
    <section className="section" id="chronology">
      <div className="section-eyebrow">Section 01</div>
      <h2 className="section-title">Incident Chronology</h2>
      <p className="section-sub">
        A reconstructed operational timeline of the Mastercard Travel & Lifestyle Services
        onboarding failure sequence observed across India region support infrastructure
        during May 2026.
      </p>

      <div className="section-divider">
        <span>Twelve documented events</span>
        <span>·</span>
        <span>Six days</span>
      </div>

      <div className="timeline">
        <span className="tl-rail" />
        {TIMELINE.map((item, i) => (
          <TimelineEntry key={i} item={item} />
        ))}
      </div>
      <div className="font-mono text-[9px] text-[var(--muted)] opacity-40 mt-[24px]" style={{ color: "hsl(var(--muted))" }}>Last Updated: 12 June 2026</div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Real evidence rendered inside cinematic dossier frames             */
/* ------------------------------------------------------------------ */

function ArtifactImage({ src, tall, fit, corner, stamp, stampTone = "gold", alt }) {  return (
    <div className={`artifact ${tall ? "is-tall" : ""}`}>
      <img
        src={src}
        alt={alt || (corner ? `${corner}${stamp ? ` — ${stamp}` : ""}` : "Documented evidence artefact")}
        className={`artifact-img ${fit ? "artifact-img-fit" : ""}`}
        loading="lazy"
        draggable={false}
      />
      {corner && <div className="artifact-corner">{corner}</div>}
      {stamp && (
        <div
          className="artifact-stamp"
          style={{
            borderColor: stampTone === "red" ? "rgba(198, 71, 82, 0.6)" : "rgba(200, 169, 106, 0.6)",
            color: stampTone === "red" ? "#e9a4ab" : "#d4af6e",
          }}
        >
          {stamp}
        </div>
      )}
    </div>
  );
}

function ArtifactPricing({ price, source, note, tone = "ok" }) {
  return (
    <div className="artifact">
      <div className="artifact-corner">{source}</div>
      <div className="absolute inset-x-5 top-12 grid gap-3">
        <div className="font-mono text-[10px] text-[var(--gold-dim)] tracking-[0.22em]">
          NOVOTEL GOA · 4 NIGHTS
        </div>
        <div className={`font-semibold text-[34px] tracking-tight ${tone === "alert" ? "text-[#e9a4ab]" : "text-[var(--ink)]"}`}>
          {price}
        </div>
        <div className="text-[11px] text-[var(--ink-muted)] leading-snug italic font-serif">
          {note}
        </div>
        <div className="mt-3 h-1.5 bg-[var(--line)] relative overflow-hidden">
          <span className={`absolute left-0 top-0 bottom-0 ${tone === "alert" ? "bg-[var(--red)]" : "bg-[var(--gold)]"}`} style={{ width: tone === "alert" ? "92%" : "62%" }} />
        </div>
      </div>
      <div className="artifact-stamp" style={{ borderColor: tone === "alert" ? "rgba(198, 71, 82, 0.6)" : "rgba(200, 169, 106, 0.6)", color: tone === "alert" ? "#e9a4ab" : "#d4af6e" }}>
        {tone === "alert" ? "POST-DELAY" : "BOOKING WINDOW"}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function DossierCard({ critical, children, evidenceRef, title, source, timestamp, redact, captionItalic }) {
  return (
    <div className={`dossier-card reveal-fade ${critical ? "is-critical" : ""}`}>
      {children}
      <div className="dossier-meta">
        <div className="dossier-title">{title}</div>
        {evidenceRef && <div className="dossier-row"><span className="k">Reference</span><span className="v">{evidenceRef}</span></div>}
        {source && <div className="dossier-row"><span className="k">Source</span><span className="v">{source}</span></div>}
        {timestamp && <div className="dossier-row"><span className="k">Captured</span><span className="v">{timestamp}</span></div>}
        {redact && <div className="dossier-row"><span className="k" style={{color:'#e9a4ab'}}>Redacted</span><span className="v">{redact}</span></div>}
        {captionItalic && <div className="dossier-caption">"{captionItalic}"</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function SectionDeadlock() {
  return (
    <section className="section" id="deadlock" style={{paddingTop:'40px'}}>
      <div className="section-eyebrow">Section 02</div>
      <h2 className="section-title">The Concierge Deadlock Architecture</h2>
      <p className="section-sub">
        "We can't help you register until you are registered." A recursive support state —
        stable, self-reinforcing, and formally unreachable.
      </p>

      <div className="deadlock-grid">
        {[
          { ix: "State 01", name: "Platform Crash", desc: "MTLS registration fails on submission. Error message instructs: call support." },
          { ix: "State 02", name: "Support Unreachable", desc: "India ITFS line returns persistent routing failure. All three published lines inaccessible." },
          { ix: "State 03", name: "WhatsApp Deadlock", desc: "Concierge unable to locate or assist without an existing profile. Redirects to support." },
        ].map((s, i, arr) => (
          <div key={i} className="deadlock-state">
            <div className="ix">{s.ix}</div>
            <div className="name">{s.name}</div>
            <div className="desc">{s.desc}</div>
            {i < arr.length - 1 && <span className="arrow">→</span>}
          </div>
        ))}
      </div>

      <div className="deadlock-loop">
        ↩ Loop Active &nbsp;·&nbsp; Return to State 01 &nbsp;·&nbsp; No exit condition through standard self-service pathways
      </div>

      <div className="mt-16">
        <div className="eyebrow-line">Evidence A · Concierge Transcript</div>
        <div className="dossier-grid">
          <DossierCard
            evidenceRef="Evidence A"
            title="Concierge Transcript · WhatsApp Business Account"
            source="Mastercard Travel & Lifestyle Services · WhatsApp"
            timestamp="05 May 2026 · 11:49 – 11:55"
            redact="cardholder name, registered phone number"
            captionItalic="Unfortunately, we're unable to locate your Mastercard Travel & Lifestyle Services profile at this moment by using the information you provided to me. Please feel free to call our hotline…"
          >
            <ArtifactImage src={EVIDENCE.whatsapp} tall corner="WhatsApp · 11:54" stamp="REDACTED" alt="Screenshot — Mastercard Travel & Lifestyle Services WhatsApp concierge transcript, 05 May 2026, 11:54. Concierge confirms inability to locate the MTLS profile and redirects the cardholder to the hotline." />
          </DossierCard>

          <div className="self-center">
            <div className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-[var(--gold-dim)] mb-4">Operational Observation</div>
            <p className="text-[16px] text-[var(--ink)] leading-relaxed">
              A profile was required to receive concierge assistance. Concierge assistance was required
              to create the profile. The published resolution pathway — calling the ITFS number — was
              simultaneously unavailable.
            </p>
            <p className="text-[14px] text-[var(--ink-muted)] leading-relaxed mt-4">
              The support architecture entered a stable recursive state. The ecosystem demonstrated
              structural completeness: every available pathway converged toward the same outcome.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
function SectionRCA() {
  return (
    <section className="section" id="evidence">
      <div className="section-eyebrow">Section 03</div>
      <h2 className="section-title">The Typing-Speed Root Cause Analysis</h2>
      <p className="section-sub">
        Official technology team finding — communicated verbally on a recorded support call at
        timestamp 22:07, delivered as a postscript after farewells had been exchanged.
      </p>

      <div className="dossier-grid mt-12">
        <DossierCard
          critical
          evidenceRef="Evidence B"
          title="Registration Failure Capture · MTLS Onboarding"
          source="travel.mastercard.com"
          timestamp="05 May 2026 · 11:20"
          redact="address line, city, postcode"
          captionItalic="All fields populated via browser autofill. Completion: < 1 second. Server returned: 'Sorry, something went wrong.'"
        >
          <ArtifactImage src={EVIDENCE.form} tall corner="travel.mastercard.com" stamp="ERROR · 11:20" stampTone="red" alt="Screenshot — MTLS registration form on travel.mastercard.com returning the server error 'Sorry, something went wrong.' on 05 May 2026 at 11:20 IST." />
        </DossierCard>

        <div className="self-center">
          <div className="rca-quote">
            <div className="label">Official Finding · Technology Team</div>
            <div className="q">
              "The error was caused due to a delay in completion of the form. In short, the page
              actually timed out."
            </div>
            <div className="meta">
              — Mastercard Technology Team &nbsp;·&nbsp; relayed by MTLS Lifestyle Manager
              &nbsp;·&nbsp; Recorded call &nbsp;·&nbsp; 22:07 timestamp
            </div>
          </div>

          <div className="mt-8 p-6 border border-[var(--line)] bg-[rgba(241,236,224,0.02)]">
            <div className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-[var(--gold-dim)]">Rebuttal · On Record</div>
            <p className="mt-3 text-[14px] text-[var(--ink-muted)] leading-relaxed">
              ~50 attempts. Multiple browsers. Multiple devices. Multiple ISPs. Form populated via
              autofill in under one second. Server-side error observed immediately upon submission.
            </p>
            <p className="mt-3 text-[14px] text-[var(--ink-muted)] leading-relaxed">
              The technology team was informed of all 50 attempts during the recorded call. The
              finding was not revised.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-10 p-6 border border-dashed border-[var(--line)] bg-[rgba(241,236,224,0.015)]">
        <p className="font-serif italic text-[18px] text-[var(--ink-muted)] leading-relaxed">
          Institutional observation — autofill completes a form in under one second. The technology
          team's finding identified this velocity as the root cause of a server-side API error. This
          represents a novel contribution to enterprise diagnostics.
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
function SectionSupport() {
  return (
    <section className="section">
      <div className="section-eyebrow">Section 04</div>
      <h2 className="section-title">Emergency Support Reliability</h2>
      <p className="section-sub uppercase tracking-[0.18em] font-mono text-[11px]" style={{maxWidth:'72ch'}}>
        Lost or Stolen Card Infrastructure
      </p>
      <p className="mt-4 text-[15px] text-[var(--ink-muted)] leading-relaxed max-w-[68ch]">
        Core payment networks depend on reliable emergency escalation pathways. During the documented
        access period, multiple attempts were made to reach published Mastercard support and
        lost/stolen card routing numbers from Jio and Airtel networks across separate dates and time
        windows.
      </p>
      <p className="mt-3 text-[15px] text-[var(--ink-muted)] leading-relaxed max-w-[68ch]">
        Observed behaviour: unsuccessful connection attempts, repeated routing failure, persistent
        call drops, inaccessible fallback pathways.
      </p>

      <div className="dossier-grid is-3 mt-12">
        <DossierCard
          critical
          evidenceRef="Call Log 01"
          title="Domestic ITFS Routing Failure"
          source="Phone · Outgoing Call History"
          timestamp="05 – 06 May 2026"
          captionItalic="Eight same-minute retries at 12:41 PM on 05 May, followed by additional attempts at 6:34 PM, 7:52 PM, and 7:08 / 7:35 PM on 06 May."
        >
          <ArtifactImage src={EVIDENCE.callLog1} tall corner="Phone · Call History" stamp="UNREACHABLE" stampTone="red" alt="Screenshot — outgoing call history showing eight same-minute retries to Mastercard India ITFS routing on 05 May 2026 at 12:41 PM, all unreachable." />
        </DossierCard>

        <DossierCard
          critical
          evidenceRef="Call Log 02"
          title="Vendor Directive Line · Routing Failure"
          source="Phone · Outgoing Call History"
          timestamp="05 May & 10 May 2026"
          captionItalic="Vendor-recommended escalation line returned identical routing failure across separate dates and time windows."
        >
          <ArtifactImage src={EVIDENCE.callLog2} tall corner="Phone · Call History" stamp="ROUTING FAIL" stampTone="red" alt="Screenshot — outgoing call history showing repeated routing failure on the vendor-recommended Mastercard escalation line across separate dates and time windows." />
        </DossierCard>

        <DossierCard
          critical
          evidenceRef="Call Log 03"
          title="Emergency Hotline · Lost & Stolen Routing Failure"
          source="Phone · Outgoing Call History"
          timestamp="05, 06 & 10 May 2026"
          captionItalic="Lost / Stolen card routing returned persistent failure across multiple dates and Indian telecom carriers."
        >
          <ArtifactImage src={EVIDENCE.callLog3} tall corner="Phone · Call History" stamp="ROUTING FAIL" stampTone="red" alt="Screenshot — outgoing call history showing persistent routing failure on the Mastercard lost / stolen card emergency hotline across multiple dates and across Jio and Airtel networks." />
        </DossierCard>
      </div>

      <div className="mt-10 p-6 border border-[var(--line)] bg-[rgba(241,236,224,0.02)]">
        <div className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-[var(--gold-dim)]">
          Concierge Recommendation · Recorded Call
        </div>
        <p className="mt-3 text-[15px] text-[var(--ink)] leading-relaxed">
          Following domestic routing failure, concierge support recommended use of the international
          routing line: <span className="font-mono text-[var(--gold)] tracking-widest">+852 3016 3977</span>. International call charges applicable to the cardholder.
        </p>
      </div>

      <div className="mt-8 p-6 border border-dashed border-[var(--line)]">
        <p className="font-serif italic text-[17px] text-[var(--ink-muted)] leading-relaxed">
          Institutional observation — the emergency fallback architecture demonstrated remarkable
          consistency across all published routing channels. Onboarding support, concierge routing,
          and emergency contact pathways converged toward the same operational outcome — providing a
          uniformly consistent cardholder experience regardless of escalation pathway chosen.
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
function SectionPricing() {
  return (
    <section className="section">
      <div className="section-eyebrow">Section 05</div>
      <h2 className="section-title">Market Rate Divergence</h2>
      <p className="section-sub">
        The concierge-assisted booking pathway demonstrated materially higher quoted pricing relative
        to independently captured public market rates recorded earlier within the same incident
        window. Same property &nbsp;·&nbsp; same room category &nbsp;·&nbsp; same four-night date window.
      </p>

      <div className="pricing-table">
        <div className="pricing-row">
          <div className="l">Rate observed during booking window</div>
          <div className="r">₹20,706</div>
        </div>
        <div className="pricing-row">
          <div className="l">Platform status at observation</div>
          <div className="r alert">INACCESSIBLE</div>
        </div>
        <div className="pricing-row">
          <div className="l">Rate quoted post-resolution (MTLS)</div>
          <div className="r alert">₹27,980 – ₹30,948</div>
        </div>
        <div className="pricing-row">
          <div className="l">Accessibility differential</div>
          <div className="r cost">~₹10,000</div>
        </div>
      </div>

      <p className="mt-6 font-serif italic text-[17px] text-[var(--ink-muted)] leading-relaxed max-w-[68ch]">
        Dynamic Outage Pricing™ — a monetisation layer where support inaccessibility becomes a
        billable feature of the travel experience.
      </p>

      <div className="mt-16">
        <div className="eyebrow-line">Manual Concierge Quote vs Public Booking Market Rate</div>
        <h3 className="mt-5 font-semibold text-[var(--ink)] tracking-tight" style={{ fontSize: "clamp(24px, 4vw, 36px)", letterSpacing: "-0.015em" }}>
          A side-by-side comparison of captured market evidence
        </h3>
        <p className="mt-4 max-w-[68ch] text-[15px] text-[var(--ink-muted)] leading-relaxed">
          The manually assisted concierge pathway resulted in materially higher quoted pricing
          compared to publicly accessible booking channels captured earlier during the incident
          window. Both captures reference the same property, the same room category and the same
          4-night date window.
        </p>

        <div className="dossier-grid mt-10">
          <DossierCard
            evidenceRef="Evidence G"
            title="Public Market Rate Capture · Booking Window"
            source="Public booking channel · 4-night total"
            timestamp="05 May 2026 · pre-failure window"
            redact="third-party platform identifiers"
            captionItalic="Hurry! Hotel price may change in 19:45 minutes — captured market rate evidence, total inclusive of taxes."
          >
            <ArtifactImage src={EVIDENCE.publicRate} tall corner="Public booking channel" stamp="BASELINE · ₹20,706" alt="Screenshot — public booking channel showing Novotel Goa Resort & Spa total of ₹20,706 for 4 nights (25–29 July 2026), captured 05 May 2026 with a 19:45 minute price hold." />
          </DossierCard>

          <DossierCard
            critical
            evidenceRef="Evidence H"
            title="Concierge Pricing Capture · MTLS Platform"
            source="travel.mastercard.com · Manual quoted flow"
            timestamp="post manual profile provisioning"
            redact="cardholder personalisation, session identifiers"
            captionItalic="Quoted booking totals on the concierge-assisted MTLS flow displayed substantially higher pricing than the captured public-market baseline."
          >
            <ArtifactImage src={EVIDENCE.conciergeRate} tall corner="MTLS Platform" stamp="QUOTED · ₹27,980 – ₹30,948" stampTone="red" alt="Screenshot — concierge-assisted MTLS quote for the same Novotel Goa property, same room category, same 4-night window, displaying ₹27,980 – ₹30,948, materially higher than the public-market baseline." />
          </DossierCard>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          <div className="p-5 border border-[var(--line)] bg-[rgba(241,236,224,0.02)]">
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--gold-dim)]">Public Baseline</div>
            <div className="mt-2 text-[26px] font-semibold tracking-tight text-[var(--ink)]">₹20,706</div>
            <div className="mt-1 font-mono text-[10.5px] tracking-[0.06em] text-[var(--ink-faint)]">Evidence G · 05 May 2026</div>
          </div>
          <div className="p-5 border border-[rgba(198,71,82,0.28)] bg-[rgba(20,4,6,0.5)]">
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#e9a4ab]">Concierge Quote</div>
            <div className="mt-2 text-[26px] font-semibold tracking-tight text-[#f0c5ca]">₹27,980 – ₹30,948</div>
            <div className="mt-1 font-mono text-[10.5px] tracking-[0.06em] text-[var(--ink-faint)]">Evidence H · post-resolution</div>
          </div>
          <div className="p-5 border border-dashed border-[var(--gold-dim)] bg-[rgba(200,169,106,0.04)]">
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--gold-dim)]">Differential</div>
            <div className="mt-2 text-[26px] font-semibold tracking-tight text-[var(--gold)]">~ ₹10,000</div>
            <div className="mt-1 font-mono text-[10.5px] tracking-[0.06em] text-[var(--ink-faint)]">Same property · same dates</div>
          </div>
        </div>
      </div>

      <div className="mt-10 p-6 border-l-2 border-[var(--red)] bg-[rgba(20,4,6,0.5)]">
        <p className="text-[15px] text-[var(--ink-muted)] leading-relaxed">
          During a recorded call, a Mastercard Travel & Lifestyle Services representative suggested
          the cardholder secure the booking through a third-party booking platform to obtain a more
          competitive rate than MTLS could provide.
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
function SectionEscalation() {
  return (
    <section className="section" id="escalation">
      <div className="section-eyebrow">Section 06</div>
      <h2 className="section-title">Acknowledged at the Highest Levels</h2>
      <p className="section-sub">
        Executive visibility was successfully achieved. Operational reversal was not.
      </p>

      <div className="escal-flow">
        <div className="escal-step">
          <div className="escal-date">6 May 2026</div>
          <div className="escal-name">Ka-Wai Au</div>
          <div className="escal-role">Executive Vice President · Global Customer Delivery & Care · CC'd on escalation email</div>
          <span className="escal-status no">No Response</span>
        </div>
        <div className="text-center font-mono text-[11px] tracking-[0.3em] text-[var(--ink-faint)]">↓</div>
        <div className="escal-step is-redirected">
          <div className="escal-date">9 May 2026</div>
          <div className="escal-name">Michael Miebach</div>
          <div className="escal-role">Chief Executive Officer · Mastercard · Direct CEO escalation submitted</div>
          <span className="escal-status redir">Redirected</span>
        </div>
        <div className="text-center font-mono text-[11px] tracking-[0.3em] text-[var(--ink-faint)]">↓</div>
        <div className="escal-step is-review">
          <div className="escal-date">10 May 2026</div>
          <div className="escal-name">Sayo</div>
          <div className="escal-role">Global Customer Delivery & Care · Mastercard Executive Escalation Team · Direct Mastercard employee</div>
          <span className="escal-status review">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--red)] mr-2 align-middle shadow-[0_0_10px_var(--red-glow)]" />
            Under Review
          </span>
          <div className="mt-6 pl-4 border-l-2 border-[var(--red)]">
            <p className="font-serif italic text-[18px] text-[var(--ink)] leading-relaxed">
              "The review remains ongoing with our internal teams and service provider. We will
              follow up once this work has concluded and the next steps have been determined."
            </p>
            <p className="mt-3 font-mono text-[10.5px] tracking-[0.18em] text-[var(--ink-faint)]">
              — Sayo · Executive Escalation Team · Global Customer Delivery & Care · Mastercard · 10 May 2026
            </p>
          </div>
        </div>
      </div>

      <div className="mt-12 p-6 border border-dashed border-[var(--line)]">
        <p className="font-serif italic text-[15px] text-[var(--ink-muted)] leading-relaxed">
          Note — the Executive Escalation team is a direct Mastercard internal function, not a
          third-party vendor. Its stated purpose is to support service-related inquiries received by
          the CEO and global executive offices.
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
function SectionAccountability() {
  return (
    <section className="section">
      <div className="section-eyebrow">Section 07</div>
      <h2 className="section-title">The Distributed Accountability Model</h2>
      <p className="section-sub">
        Premium branding. Outsourced infrastructure. Accountability distributed across sufficient
        organisational layers to achieve equilibrium.
      </p>

      <div className="acct-grid">
        <div className="acct-card">
          <div className="label">Brand Licensor · Platform Owner</div>
          <div className="name">Mastercard</div>
          <div className="role">Global payment network · World tier programme</div>
          <div className="meta">Executive Visibility: <span className="text-[var(--gold)]">HIGH</span></div>
        </div>
        <div className="acct-arrow">→ licenses to →</div>
        <div className="acct-card" style={{borderLeft:'2px solid var(--red)'}}>
          <div className="label" style={{color:'#e9a4ab'}}>Operational Vendor · LSE: TENL</div>
          <div className="name">Ten Lifestyle Group plc</div>
          <div className="role">MTLS infrastructure · Concierge operations · Booking fulfilment</div>
          <div className="meta">Accountability: <span style={{color:'#e9a4ab'}}>DISTRIBUTED</span></div>
        </div>
      </div>

      <div className="mt-12 p-6 border border-[var(--line)] bg-[rgba(241,236,224,0.02)]">
        <div className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-[var(--gold-dim)]">
          From Ten Lifestyle Group's own email footer · Present in every MTLS correspondence
        </div>
        <p className="mt-4 font-serif italic text-[17px] text-[var(--ink)] leading-relaxed">
          "Mastercard® Travel & Lifestyle Services are provided by Ten Lifestyle Group plc. No travel
          bookings are being made by Mastercard, nor is Mastercard acting as a travel agency or
          providing any travel consultation or advice, in connection with Mastercard Travel &
          Lifestyle Services."
        </p>
        <p className="mt-3 text-[13px] text-[var(--ink-muted)]">
          This disclaimer appears below every MTLS email — including emails sent in response to
          documented platform failures and executive escalations.
        </p>
      </div>

      <div className="mt-8 p-6 border-l-2 border-[var(--gold-dim)] bg-[rgba(241,236,224,0.015)]">
        <p className="font-serif italic text-[17px] text-[var(--ink-muted)] leading-relaxed">
          Institutional observation — the organisational architecture routes operational friction
          away from core infrastructure ownership and into third-party escalation channels. The
          liability disclaimer is pre-positioned in every customer interaction. The issue travelled
          further internally than the customer did externally.
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
const METRICS = [
  ["Branding Confidence", "Excellent", "good"],
  ["Frontline Professionalism", "Strong", "good"],
  ["Platform Stability", "Philosophical", "warn"],
  ["Domestic ITFS Routing", "Unreachable", "bad"],
  ["Lost / Stolen Card Line", "Intermittently Theoretical", "bad"],
  ["Concierge Assistance", "Recursive", "bad"],
  ["Executive Visibility", "High", "good"],
  ["Executive Interactivity", "Limited", "warn"],
  ["Resolution Velocity", "Under Review", "warn"],
  ["Accountability Localisation", "Distributed", "neutral"],
  ["Concierge Booking Recommendation", "Use Third-Party Platforms", "bad"],
];

function SectionMetrics() {
  return (
    <section className="section" id="metrics">
      <div className="section-eyebrow">Section 08</div>
      <h2 className="section-title">Operational Metrics Dashboard</h2>
      <p className="section-sub">
        Mastercard World &nbsp;·&nbsp; India Region &nbsp;·&nbsp; May 2026
      </p>

      <div className="metrics-table">
        {METRICS.map(([m, s, tone], i) => (
          <div key={i} className="metrics-row">
            <div className="m">{m}</div>
            <div className={`s ${tone}`}>{s}</div>
          </div>
        ))}
      </div>

      <div className="mt-12 p-8 border border-[rgba(198,71,82,0.18)] bg-[rgba(20,6,8,0.45)] border-l-2 border-l-[var(--red)] shadow-[0_30px_80px_-40px_rgba(198,71,82,0.5)]">
        <div className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-[var(--red)]">
          Institutional Observation
        </div>
        <p className="mt-4 font-serif italic text-[clamp(18px,3.5vw,22px)] text-[var(--ink)] leading-relaxed">
          Across six days and twelve documented operational events, every published self-service and
          support pathway failed to deliver resolution. The onboarding issue was eventually bypassed
          through manual vendor intervention. The underlying operational reliability concerns remain
          under review.
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
function SectionDisclaimer() {
  return (
    <section className="section" id="disclaimer" style={{ paddingTop: "20px" }}>
      <div className="section-eyebrow">Notice</div>
      <h2 className="section-title" style={{ fontSize: "clamp(28px, 5vw, 52px)" }}>
        Editorial Notice & Disclaimer
      </h2>
      <p className="section-sub">
        On the nature, intent, and independence of this microsite.
      </p>

      <div className="mt-10 grid gap-6 max-w-[78ch]">
        <div className="p-6 md:p-8 border border-[var(--line)] bg-[rgba(241,236,224,0.02)]">
          <div className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-[var(--gold-dim)]">
            Purpose
          </div>
          <p className="mt-4 text-[15px] text-[var(--ink)] leading-relaxed">
            This microsite is an independent editorial commentary and documented case study
            describing a specific, observed customer-experience sequence. It is published in the
            public interest, in the spirit of operational accountability journalism, and is intended
            to inform readers, researchers and service-design practitioners of the observed events.
          </p>
        </div>

        <div className="p-6 md:p-8 border border-[var(--line)] bg-[rgba(241,236,224,0.02)]">
          <div className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-[var(--gold-dim)]">
            Independence & Affiliation
          </div>
          <p className="mt-4 text-[15px] text-[var(--ink-muted)] leading-relaxed">
            This site is not affiliated with, endorsed by, sponsored by, or otherwise associated
            with <span className="text-[var(--ink)]">Mastercard</span>,{" "}
            <span className="text-[var(--ink)]">Ten Lifestyle Group plc</span>,{" "}
            <span className="text-[var(--ink)]">HDFC Bank</span>, or any of their affiliates,
            officers, agents, vendors or service providers. All company names, trade marks, service
            marks and product names referenced are the property of their respective owners and are
            used here solely for the purposes of identification, accurate reporting, and fair
            comment.
          </p>
        </div>

        <div className="p-6 md:p-8 border border-[var(--line)] bg-[rgba(241,236,224,0.02)]">
          <div className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-[var(--gold-dim)]">
            Evidence & Accuracy
          </div>
          <p className="mt-4 text-[15px] text-[var(--ink-muted)] leading-relaxed">
            Supporting artefacts presented within this microsite are reproductions of contemporaneous
            records held by the author, redacted to remove personally identifiable information.
            Quotations are reproduced as observed; paraphrasing is indicated. Timestamps reflect the
            local time zone of the observed events. The author welcomes good-faith corrections from
            any party named or referenced.
          </p>
        </div>

        <div className="p-6 md:p-8 border border-[var(--line)] bg-[rgba(241,236,224,0.02)]">
          <div className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-[var(--gold-dim)]">
            Scope of Commentary
          </div>
          <p className="mt-4 text-[15px] text-[var(--ink-muted)] leading-relaxed">
            Statements presented as observations, characterisations or commentary represent the
            author's opinions and reasonable inferences drawn from the documented record. They are
            not assertions of legal liability. Nothing on this site constitutes legal, financial or
            consumer-protection advice. Readers seeking redress are encouraged to consult the
            appropriate consumer or regulatory authority in their jurisdiction.
          </p>
        </div>

        <div className="p-6 md:p-8 border border-dashed border-[var(--line)]">
          <p className="font-serif italic text-[14px] text-[var(--ink-faint)] leading-relaxed">
            Should any party named or referenced wish to provide additional context, an update, or a
            formal response, the author will consider such submissions in good faith and update the
            record accordingly.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
function Footer() {
  return (
    <footer className="site-footer">
      <div className="inner">
        <div>Premium Latency<sup className="font-mono text-[var(--gold)]">TM</sup>  ·  <strong>An Independent Documented Operational Case Study</strong></div>
        <div>Observed Period: 05 May – 10 May 2026  ·  India Region</div>
        <div>Editorial commentary &amp; documentation only. Not affiliated with <strong>Mastercard</strong>, <strong>Ten Lifestyle Group plc</strong> or <strong>HDFC Bank</strong>.</div>
        <div className="opacity-60">All company names and marks remain the property of their respective owners. See Editorial Notice above.</div>
        <div className="flex gap-4 mt-4 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--gold)]">
          <a href="/privacy" className="hover:text-[var(--gold-glow)] transition-colors">Privacy Policy</a>
          <span className="text-[var(--line)]">·</span>
          <a href="/terms" className="hover:text-[var(--gold-glow)] transition-colors">Terms & Conditions</a>
        </div>
      </div>
    </footer>
  );
}

/* ------------------------------------------------------------------ */
function useRevealOnScroll() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal, .reveal-fade");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function ExistingDashboard() {
  useRevealOnScroll();
  return (
    <div className="App">
      <div className="page-vignette" />
      <div className="page-grain" />
      <NavBar />
      <main role="main" id="main">
        <Hero />
        <SectionChronology />
        <SectionDeadlock />
        <SectionRCA />
        <SectionSupport />
        <SectionPricing />
        <SectionEscalation />
        <SectionAccountability />
        <SectionMetrics />
        <SectionDisclaimer />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  const [showAudit, setShowAudit] = useState(false);
  // build-id: stabilization-pass-2026-05-14
  const handleUnlock = () => {
    // Force-disable smooth scrolling momentarily, then jump to top so the
    // dashboard hero is in view when the audit mounts after the transition.
    const html = document.documentElement;
    const prevScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    try {
      window.scrollTo(0, 0);
      html.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    } catch (_e) { /* noop */ }
    // Restore after the AnimatePresence transition window (~0.5s) plus a margin.
    window.setTimeout(() => {
      html.style.scrollBehavior = prevScrollBehavior;
    }, 800);
    setShowAudit(true);
  };

  // After audit mounts, ensure scroll is at the top across the transition
  // window — fires immediately, on next frame and again after the fade.
  useEffect(() => {
    if (!showAudit) return;
    const html = document.documentElement;
    const reset = () => {
      try {
        window.scrollTo(0, 0);
        html.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      } catch (_e) {}
    };
    reset();
    const raf = window.requestAnimationFrame(reset);
    const t1 = window.setTimeout(reset, 60);
    const t2 = window.setTimeout(reset, 600);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [showAudit]);

  return (
    <>
      <AnimatePresence mode="wait">
        {!showAudit ? (
          <motion.div
            key="cover"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <CoverScreen onUnlock={handleUnlock} />
          </motion.div>
        ) : (
          <motion.div
            key="audit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ExistingDashboard />
          </motion.div>
        )}
      </AnimatePresence>
      <SpeedInsights />
      <Analytics />
    </>
  );
}

export default App;
